/**
 * Tools view component.
 * @module components/View/ToolsView
 */

import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Input,
  Label,
  Table,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  TableHeaderCell,
} from 'semantic-ui-react';
import { Link, useHistory } from 'react-router-dom';
import { map } from 'lodash';

import { Icon } from '@plone/volto/components';
import { flattenToAppURL } from '@plone/volto/helpers';

import downSVG from '@plone/volto/icons/down-key.svg';
import upSVG from '@plone/volto/icons/up-key.svg';
import clearSVG from '@plone/volto/icons/clear.svg';
import addSVG from '@plone/volto/icons/add.svg';

/**
 * Tools view component class.
 * @function ToolsView
 * @param {Object} content Content object.
 * @returns {string} Markup of the component.
 */
const ToolsView = ({ content }) => {
  const history = useHistory();
  let items = [...content.items];

  items = map(items, (item) => ({
    ...item,
    fullcode: item.code_variant
      ? `${item.code}.${item.code_variant}`
      : item.code,
  }));

  // Filtering
  const [fullcode, setFullcode] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');

  const fullcodeRef = useRef(null);
  const nameRef = useRef(null);
  const categoryRef = useRef(null);

  const filterField = (items, field, value) =>
    value === ''
      ? items
      : (items = items.filter(
          (item) =>
            item[field].toLowerCase().indexOf(value.toLowerCase()) !== -1,
        ));

  items = filterField(items, 'fullcode', fullcode);
  items = filterField(items, 'name', name);
  items = filterField(items, 'category', category);

  // Sorting
  const [sort, setSort] = useState('fullcode');
  const [dir, setDir] = useState('asc');

  items = items.sort((a, b) =>
    (dir === 'asc' ? a[sort] > b[sort] : a[sort] < b[sort]) ? 1 : -1,
  );

  const onSort = (value) => {
    if (sort === value) {
      setDir(dir === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(value);
      setDir('asc');
    }
  };

  const SortIcon = () => (
    <Icon
      name={dir === 'desc' ? upSVG : downSVG}
      className="icon"
      size="20px"
    />
  );

  return (
    <article id="tools">
      <div className="button-toolbar">
        <Link
          className="circular-button primary float-left"
          to={`${flattenToAppURL(content['@id'])}/add?type=Tool`}
        >
          <Icon name={addSVG} size="24px" />
        </Link>
        <div className="button-toolbar-title">
          Tools <Label circular>{items.length}</Label>
        </div>
      </div>
      <div className="view-padding">
        <div className="scrollable-table">
          <Table striped celled selectable>
            <TableHeader>
              <TableRow className="tools-header">
                <TableHeaderCell onClick={() => onSort('fullcode')} width="2">
                  Code {sort === 'fullcode' && <SortIcon />}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => onSort('name')} width="6">
                  Name {sort === 'name' && <SortIcon />}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => onSort('category')} width="4">
                  Category {sort === 'category' && <SortIcon />}
                </TableHeaderCell>
              </TableRow>
              <TableRow className="tools-filter">
                <TableHeaderCell>
                  <div className="filter-input">
                    <Input
                      value={fullcode}
                      placeholder="Filter code"
                      autoCorrect="off"
                      spellCheck="false"
                      ref={fullcodeRef}
                      onChange={(e) =>
                        setFullcode(e.target.value.toUpperCase())
                      }
                    />
                    {fullcode && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => {
                          setFullcode('');
                          fullcodeRef.current?.focus();
                        }}
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell>
                  <div className="filter-input">
                    <Input
                      value={name}
                      placeholder="Filter name"
                      autoCorrect="off"
                      spellCheck="false"
                      ref={nameRef}
                      onChange={(e) => setName(e.target.value)}
                    />
                    {name && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => {
                          setName('');
                          nameRef.current?.focus();
                        }}
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell>
                  <div className="filter-input">
                    <Input
                      value={category}
                      placeholder="Filter category"
                      autoCorrect="off"
                      spellCheck="false"
                      ref={categoryRef}
                      onChange={(e) => setCategory(e.target.value)}
                    />
                    {category && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => {
                          setCategory('');
                          categoryRef.current?.focus();
                        }}
                      />
                    )}
                  </div>
                </TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow
                  className="clickable-row"
                  onClick={() => history.push(flattenToAppURL(item['@id']))}
                >
                  <TableCell>{item.fullcode}</TableCell>
                  <TableCell>
                    {item.name}
                    {item.name_variant ? ` (${item.name_variant})` : ''}
                  </TableCell>
                  <TableCell>{item.category}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </article>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
ToolsView.propTypes = {
  content: PropTypes.shape({
    code: PropTypes.string,
    name: PropTypes.string,
    category: PropTypes.string,
  }).isRequired,
};

export default ToolsView;
